import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Alert,
  Tooltip,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Paper,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import InfoIcon from "@mui/icons-material/Info";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import WarningIcon from "@mui/icons-material/Warning";
import ReplayIcon from "@mui/icons-material/Replay";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate, useSearchParams } from "react-router-dom";
import Color from "../../components/Color";
import { useSnackbar } from "../../App";
import {
  getAllDeleteRequest,
  approveDeleteRequest,
  rejectDeleteRequest,
} from "../../service/orderService";
import {
  getPendingPaymentApprovals,
  approvePayment,
  rejectPayment,
} from "../../service/paymentService";
import { getAllBranches } from "../../service/branchService";

// ==========================================
// 1. ORDER APPROVAL SECTION
// ==========================================
const OrderApprovalSection = ({ onCountChange }) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0); // 0: All, 1: Pending, 2: Approved, 3: Refunded

  // Step 1 modal: shown when Approve button is clicked — has Re-order & Refund buttons
  const [approveModalOpen, setApproveModalOpen] = useState(false);

  // Step 2a modal: Refund details (opened from step 1 modal)
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);

  // Reject modal
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  // View details modal for approved/refunded cancel requests
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [percentage, setPercentage] = useState("");
  const [reorderLoading, setReorderLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const triggerSidebarRefresh = () => {
    window.dispatchEvent(new CustomEvent("sidebarRefresh"));
  };

  /** Fetch pending delete requests */
  const fetchPendingDeleteRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllDeleteRequest({ limit: 0 });
      const data = res?.data?.data?.data;

      if (Array.isArray(data)) {
        const normalizedRows = data.map((row) => ({
          ...row,
          customerName: row.customer?.name || "",
          branchName: row.branch?.name || "",
          order_date: row.order_date ? row.order_date.split("T")[0] : "",
        }));
        setRows(normalizedRows);
        const pendingCount = normalizedRows.filter(
          (r) => r.status === "request_for_cancel"
        ).length;
        if (onCountChange) onCountChange(pendingCount);
      } else {
        setRows([]);
        showSnackbar(
          "Unexpected data format for pending cancel requests!",
          "error"
        );
      }
    } catch (error) {
      console.error("Fetch pending cancel requests error:", error);
      showSnackbar(
        error?.response?.data?.message ||
          "Failed to fetch pending cancel requests!",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, onCountChange]);

  useEffect(() => {
    fetchPendingDeleteRequests();
  }, [fetchPendingDeleteRequests]);

  /** Get the status of the order before request_for_cancel */
  const getPreviousStatus = useCallback((row) => {
    if (row?.previous_status) return row.previous_status;
    const history = row?.status_history || [];
    if (!history.length) return null;
    const sorted = [...history].sort(
      (a, b) =>
        new Date(b.created_at || b.updated_at) -
        new Date(a.created_at || a.updated_at)
    );
    const prevRow = sorted.find((h) => h.status !== "request_for_cancel");
    return prevRow ? prevRow.status : null;
  }, []);

  const previousStatus = selectedRow ? getPreviousStatus(selectedRow) : null;

  const formatStatus = (status) => {
    if (!status) return "";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const getViewRefundDetails = useCallback((row) => {
    if (!row) return { paidBeforeRefund: 0, refundAmt: 0, refundPct: 0, netPaid: 0 };
    const payments = Array.isArray(row.payments) ? row.payments : [];

    const refundPayment = payments.find(
      (p) => parseFloat(p.amount) < 0 || p.payment_method === "refund"
    );
    const refundAmt = refundPayment ? Math.abs(parseFloat(refundPayment.amount)) : 0;

    const paidBeforeRefund = payments
      .filter((p) => p !== refundPayment && parseFloat(p.amount) > 0)
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

    const netPaid = paidBeforeRefund - refundAmt;

    let refundPct = 0;
    if (refundPayment) {
      const match = (refundPayment.notes || "").match(/Refund of (\d+(?:\.\d+)?)%/);
      if (match && match[1]) {
        refundPct = parseFloat(match[1]);
      } else if (paidBeforeRefund > 0) {
        refundPct = Math.round((refundAmt / paidBeforeRefund) * 100);
      }
    }

    return { paidBeforeRefund, refundAmt, refundPct, netPaid };
  }, []);

  /** Amount calculations */
  const totalAmount = parseFloat(selectedRow?.total_amount) || 0;
  const paidAmount = Array.isArray(selectedRow?.payments)
    ? selectedRow.payments.reduce(
        (sum, p) => sum + (parseFloat(p.amount) || 0),
        0
      )
    : 0;

  const baseAmount = paidAmount;
  const parsedPercentage = parseFloat(percentage);
  const calculatedAmount =
    !isNaN(parsedPercentage) && parsedPercentage >= 0
      ? ((parsedPercentage / 100) * baseAmount).toFixed(2)
      : null;

  /** Open step-1 approve modal */
  const openApproveModal = (row) => {
    setDeleteId(row.id);
    setSelectedRow(row);
    setApproveModalOpen(true);
  };

  /** From step-1 modal: user chose Refund → open refund dialog */
  const handleChooseRefund = () => {
    setApproveModalOpen(false);

    // Pre-fill percentage based on previous status
    const prev =
      selectedRow?.previous_status ||
      (() => {
        const history = selectedRow?.status_history || [];
        const sorted = [...history].sort(
          (a, b) =>
            new Date(b.created_at || b.updated_at) -
            new Date(a.created_at || a.updated_at)
        );
        return sorted.find((h) => h.status !== "request_for_cancel")?.status;
      })();

    if (prev === "order_placed") {
      setPercentage("100");
    } else if (prev === "preparing" || prev === "ready_for_delivery") {
      setPercentage("0");
    } else {
      setPercentage("100");
    }

    setRefundDialogOpen(true);
  };

  /** From step-1 modal: user chose Re-order */
  const handleChooseReorder = async () => {
    setReorderLoading(true);
    try {
      await approveDeleteRequest(deleteId, {
        refund_percentage: 0,
        refund_amount: 0,
      });
      showSnackbar(
        "Cancel approved. Redirecting to create re-order...",
        "success"
      );

      localStorage.removeItem("selectedProducts");
      localStorage.setItem(
        "cancelReferenceOrderNumber",
        selectedRow?.order_number || ""
      );

      triggerSidebarRefresh();
      fetchPendingDeleteRequests();
      navigate("/create-order");
    } catch (error) {
      console.error("Reorder Error:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to process re-order!",
        "error"
      );
    } finally {
      setReorderLoading(false);
      setApproveModalOpen(false);
      setDeleteId(null);
      setSelectedRow(null);
    }
  };

  /** Approve & Refund (from refund dialog) */
  const handleApproveWithRefund = async () => {
    try {
      const finalPercentage =
        paidAmount === 0
          ? 0
          : !isNaN(parsedPercentage) && parsedPercentage >= 0
          ? parsedPercentage
          : 0;
      const finalAmount =
        paidAmount === 0
          ? 0
          : calculatedAmount !== null
          ? parseFloat(calculatedAmount)
          : 0;

      const payload = {
        refund_percentage: finalPercentage,
        refund_amount: finalAmount,
      };
      const response = await approveDeleteRequest(deleteId, payload);
      showSnackbar(
        response?.message || "Refund approved successfully!",
        "success"
      );
      triggerSidebarRefresh();
      fetchPendingDeleteRequests();
    } catch (error) {
      console.error("Approve Refund Error:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to approve refund!",
        "error"
      );
    } finally {
      setRefundDialogOpen(false);
      setDeleteId(null);
      setSelectedRow(null);
      setPercentage("");
    }
  };

  /** Reject cancel request */
  const handleRejectDelete = async (id) => {
    try {
      const response = await rejectDeleteRequest(id, rejectionReason);
      showSnackbar(
        response?.message || "Order cancel request rejected successfully!",
        "success"
      );
      triggerSidebarRefresh();
      fetchPendingDeleteRequests();
    } catch (error) {
      console.error("Reject Cancel Error:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to reject cancel request!",
        "error"
      );
    } finally {
      setRejectDialogOpen(false);
      setDeleteId(null);
      setSelectedRow(null);
      setRejectionReason("");
    }
  };

  const handleExportApprovedRefunded = () => {
    const getDeliveryDateTime = (deliveryDateStr) => {
      if (!deliveryDateStr) return { date: "", time: "" };
      const parts = deliveryDateStr.split(/[T ]/);
      const datePart = parts[0] || "";
      let timePart = parts[1] || "";
      if (timePart) {
        timePart = timePart.split(".")[0];
        timePart = timePart.split("+")[0];
        timePart = timePart.split("Z")[0];
      }

      let formattedDate = datePart;
      const dateSubparts = datePart.split("-");
      if (dateSubparts.length === 3) {
        const [year, month, day] = dateSubparts;
        formattedDate = `${day}-${month}-${year}`;
      }

      return { date: formattedDate, time: timePart };
    };

    const exportData = filteredRows;

    if (exportData.length === 0) {
      showSnackbar("No data to export for this view!", "info");
      return;
    }

    const headers = [
      "Order No",
      "Customer",
      "Branch",
      "Order Date",
      "Delivery Date",
      "Delivery Time",
      "Total Amount (INR)",
      "Paid Amount (INR)",
      "Refund Amount (INR)",
      "Refund Percentage (%)",
      "Status",
      "Cancel Reason",
    ];

    const csvLines = exportData.map((row) => {
      const orderNo = `"${(row.order_number || "").replace(/"/g, '""')}"`;
      const customer = `"${(row.customerName || "").replace(/"/g, '""')}"`;
      const branch = `"${(row.branchName || "").replace(/"/g, '""')}"`;
      const orderDate = `"${(row.order_date || "").replace(/"/g, '""')}"`;

      const deliveryInfo = getDeliveryDateTime(row.delivery_date);
      const deliveryDate = `"${deliveryInfo.date}"`;
      const deliveryTime = `"${deliveryInfo.time}"`;

      const total = row.total_amount || 0;

      const paid = Array.isArray(row.payments)
        ? row.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
        : 0;

      const refundPayment = Array.isArray(row.payments)
        ? row.payments.find(
            (p) => parseFloat(p.amount) < 0 || p.payment_method === "refund"
          )
        : null;

      const refundAmt = refundPayment
        ? Math.abs(parseFloat(refundPayment.amount))
        : 0;

      let refundPct = 0;
      if (refundPayment) {
        const match = (refundPayment.notes || "").match(
          /Refund of (\d+(?:\.\d+)?)%/
        );
        if (match && match[1]) {
          refundPct = parseFloat(match[1]);
        } else if (paid > 0) {
          const paidBefore = paid + refundAmt;
          refundPct =
            paidBefore > 0 ? Math.round((refundAmt / paidBefore) * 100) : 0;
        }
      }

      const statusLabel =
        row.status === "cancelled"
          ? "Approved & Cancelled"
          : row.status === "refunded"
          ? "Refunded"
          : row.status === "partially_refunded"
          ? "Partially Refunded"
          : formatStatus(row.status);
      const cancelReason = `"${(row.cancel_remarks || "")
        .replace(/\r?\n/g, " ")
        .replace(/"/g, '""')}"`;

      return [
        orderNo,
        customer,
        branch,
        orderDate,
        deliveryDate,
        deliveryTime,
        total,
        paid,
        refundAmt.toFixed(2),
        refundPct,
        `"${statusLabel}"`,
        cancelReason,
      ].join(",");
    });

    const csvContent = "\ufeff" + [headers.join(","), ...csvLines].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;

    let filenamePrefix = "all_cancel_requests";
    if (tabValue === 1) {
      filenamePrefix = "pending_cancel_requests";
    } else if (tabValue === 2) {
      filenamePrefix = "approved_cancel_requests";
    } else if (tabValue === 3) {
      filenamePrefix = "refunded_cancel_requests";
    }

    const today = new Date();
    const formattedDate = `${today.getDate()}-${
      today.getMonth() + 1
    }-${today.getFullYear()}`;
    link.setAttribute("download", `${filenamePrefix}_${formattedDate}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showSnackbar("Exported data successfully!", "success");
  };

  // Filtered rows for DataGrid
  const filteredRows = React.useMemo(() => {
    return rows.filter((row) => {
      if (tabValue === 0) {
        return (
          row.status === "request_for_cancel" ||
          row.status === "cancelled" ||
          row.status === "refunded" ||
          row.status === "partially_refunded"
        );
      }
      if (tabValue === 1) {
        return row.status === "request_for_cancel";
      }
      if (tabValue === 2) {
        return row.status === "cancelled";
      }
      if (tabValue === 3) {
        return row.status === "refunded" || row.status === "partially_refunded";
      }
      return false;
    });
  }, [rows, tabValue]);

  const tabCounts = React.useMemo(() => {
    const all = rows.filter(
      (r) =>
        r.status === "request_for_cancel" ||
        r.status === "cancelled" ||
        r.status === "refunded" ||
        r.status === "partially_refunded"
    ).length;
    const pending = rows.filter((r) => r.status === "request_for_cancel").length;
    const approved = rows.filter((r) => r.status === "cancelled").length;
    const refunded = rows.filter(
      (r) => r.status === "refunded" || r.status === "partially_refunded"
    ).length;
    return { all, pending, approved, refunded };
  }, [rows]);

  /** Columns for DataGrid */
  const columns = [
    {
      field: "order_number",
      headerName: "Order No",
      flex: 0.9,
      minWidth: isMobile ? 100 : 100,
    },
    {
      field: "customerName",
      headerName: "Customer",
      flex: 0.8,
      minWidth: isMobile ? 120 : 130,
    },
    { field: "branchName", headerName: "Branch", flex: 0.8, minWidth: 160 },
    {
      field: "order_date",
      headerName: "Order Date",
      flex: 0.6,
      minWidth: 150,
      renderCell: (params) => {
        const val = params.value;
        if (!val) return "-";
        const cleanStr = String(val).split("T")[0].split(" ")[0];
        const parts = cleanStr.split("-");
        if (parts.length === 3) {
          const [year, month, day] = parts;
          return `${day}-${month}-${year}`;
        }
        return cleanStr;
      },
    },
    {
      field: "total_amount",
      headerName: "Total",
      flex: 0.6,
      minWidth: isMobile ? 80 : 120,
    },
    {
      field: "cancel_remarks",
      headerName: "Cancel Reason",
      flex: 1.2,
      minWidth: isMobile ? 150 : 200,
      cellClassName: "cancel-remarks-cell",
      renderCell: (params) => {
        const val = (params.value || "").trim().replace(/\n{3,}/g, "\n\n");
        if (!val || val === "-") return "-";
        const isRequestForCancel = params.row.status === "request_for_cancel";
        return (
          <Tooltip
            title={
              <Box
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontSize: "13px",
                  lineHeight: "1.4",
                  color: "#1e293b",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {val}
              </Box>
            }
            placement="top"
            PopperProps={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -42],
                  },
                },
              ],
            }}
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: isRequestForCancel ? "#ffeef4" : "#ffffff",
                  border: "1px solid",
                  borderColor: isRequestForCancel ? "#ffb6c1" : "#e2e8f0",
                  boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  maxWidth: "320px",
                },
              },
            }}
          >
            <Box
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              {val}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: isMobile ? 140 : 120,
      renderCell: (params) => {
        const status = params.value;
        let label = "Pending Cancel Approval";
        let bg = "#FFA500";
        let text = "#000000";

        const history = params.row.status_history || [];
        const isRejected = history.some(
          (h) => h.status === "cancel_request_rejected"
        );

        if (status === "cancelled") {
          label = "Approved & Cancelled";
          bg = "#dcfce7";
          text = "#166534";
        } else if (status !== "request_for_cancel") {
          label = formatStatus(status);
          if (isRejected) {
            bg = "#fee2e2";
            text = "#991b1b";
          } else {
            bg = "#f1f5f9";
            text = "#475569";
          }
        }
        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: bg,
              color: text,
              fontSize: isMobile ? "0.75rem" : "0.85rem",
              fontWeight: 500,
              borderRadius: "12px",
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      filterable: false,
      minWidth: isMobile ? 90 : 110,
      renderCell: (params) => {
        if (params.row.status !== "request_for_cancel") {
          return (
            <Tooltip title="View Details">
              <IconButton
                size="small"
                color="info"
                onClick={() => {
                  setSelectedRow(params.row);
                  setViewDialogOpen(true);
                }}
                sx={{
                  backgroundColor: "rgba(2, 136, 209, 0.08)",
                  "&:hover": { backgroundColor: "rgba(2, 136, 209, 0.2)" },
                  borderRadius: "6px",
                  p: 0.8,
                }}
              >
                <VisibilityIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
              </IconButton>
            </Tooltip>
          );
        }
        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              height: "100%",
            }}
          >
            {/* Approve IconButton */}
            <Tooltip title="Approve">
              <IconButton
                size="small"
                color="success"
                onClick={() => openApproveModal(params.row)}
                sx={{
                  backgroundColor: "rgba(46, 125, 50, 0.08)",
                  "&:hover": { backgroundColor: "rgba(46, 125, 50, 0.2)" },
                  borderRadius: "6px",
                  p: 0.8,
                }}
              >
                <CheckIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
              </IconButton>
            </Tooltip>

            {/* Reject IconButton */}
            <Tooltip title="Reject">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setDeleteId(params.row.id);
                  setSelectedRow(params.row);
                  setRejectDialogOpen(true);
                }}
                sx={{
                  backgroundColor: "rgba(211, 47, 47, 0.08)",
                  "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.2)" },
                  borderRadius: "6px",
                  p: 0.8,
                }}
              >
                <ClearIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box mb={{ xs: 0, sm: 1 }} sx={{ padding: isMobile ? 1 : 0, mt: 1 }}>
      {/* Sub Tabs and Export (Top) */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={false}
          sx={{
            width: isMobile ? "100%" : "auto",
            minHeight: "auto",
            "& .MuiTabs-scroller": {
              overflow: "visible !important",
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .MuiTabs-flexContainer": {
              justifyContent: isMobile ? "center" : "flex-start",
              gap: 1.5,
              flexWrap: isMobile ? "nowrap" : "wrap",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? "0.75rem" : "0.85rem",
              borderRadius: "8px",
              minHeight: "36px",
              height: "36px",
              padding: isMobile ? "4px 8px" : "0px 16px",
              backgroundColor: "#ffffff",
              color: "#64748b",
              border: "1px solid #e2e8f0",
              transition: "all 0.2s ease-in-out",
              minWidth: isMobile ? "auto" : "120px",
              "&:hover": {
                backgroundColor: "#f8fafc",
                borderColor: Color.secondary.main,
              },
            },
            "& .Mui-selected": {
              backgroundColor: `${Color.secondary.main} !important`,
              color: "#ffffff !important",
              borderColor: Color.secondary.main,
              boxShadow: "0 4px 12px rgba(26, 115, 232, 0.15)",
            },
          }}
        >
          <Tab value={0} label={`All (${tabCounts.all})`} />
          <Tab value={1} label={`Pending (${tabCounts.pending})`} />
          <Tab value={2} label={`Approved (${tabCounts.approved})`} />
          <Tab value={3} label={`Refunded (${tabCounts.refunded})`} />
        </Tabs>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportApprovedRefunded}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            height: "36px",
            backgroundColor: Color.secondary.main,
            color: "#ffffff",
            "&:hover": {
              backgroundColor: Color.secondary.dark,
            },
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            width: isMobile ? "100%" : "auto",
            whiteSpace: "nowrap",
          }}
        >
          Export
        </Button>
      </Box>

      {/* DataGrid */}
      <DataGrid
        rows={filteredRows}
        columns={columns}
        rowHeight={isMobile ? 48 : 56}
        loading={loading}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        disableColumnSorting
        disableColumnMenu
        getRowClassName={(params) =>
          params.row.status === "request_for_cancel"
            ? "pink-row"
            : params.indexRelativeToCurrentPage % 2 === 0
            ? "even-row"
            : "odd-row"
        }
        sx={{
          fontFamily: '"Poppins", sans-serif',
          borderRadius: "10px",
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: isMobile ? "14px" : "16px",
            fontWeight: 600,
            pl: 1,
          },
          "& .MuiDataGrid-cell": {
            fontSize: isMobile ? "12px" : "14px",
            pl: 2,
            display: "flex",
            alignItems: "center",
          },
          "& .cancel-remarks-cell": {
            overflow: "visible !important",
          },
          "& .pink-row": {
            backgroundColor: "#ffdce7 !important",
            "&:hover": {
              backgroundColor: "#ffeef4 !important",
            },
          },
          "& .even-row": { backgroundColor: "#fff" },
          "& .odd-row": { backgroundColor: "#f4f4f4" },
          "& .MuiLinearProgress-bar": { backgroundColor: Color.primary.main },
          "& .MuiLinearProgress-colorPrimary": {
            backgroundColor: Color.secondary.main,
          },
          overflowX: isMobile ? "hidden" : "auto",
        }}
      />

      {/* ==================== STEP-1: APPROVE MODAL (Re-order / Refund choice) ==================== */}
      <Dialog
        open={approveModalOpen}
        onClose={() => !reorderLoading && setApproveModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            width: isMobile ? "95vw" : "auto",
            m: isMobile ? 1 : 2,
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckIcon sx={{ color: "success.main" }} /> Approve Cancel Request
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ mb: 2.5, color: "text.secondary" }}>
            Approving cancel request for order{" "}
            <strong>{selectedRow?.order_number}</strong> of customer{" "}
            <strong>{selectedRow?.customerName}</strong>. Choose how to proceed:
          </Typography>

          {/* Cancellation remarks */}
          {selectedRow?.cancel_remarks && (
            <Box
              sx={{
                mb: 2.5,
                p: 2,
                backgroundColor: "#fff5f5",
                borderLeft: "4px solid #ef4444",
                borderRadius: "4px",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: "#991b1b",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mb: 0.5,
                }}
              >
                <WarningIcon sx={{ fontSize: 16 }} /> Cancellation Reason
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#7f1d1d",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {selectedRow.cancel_remarks}
              </Typography>
              {selectedRow?.cancel_attachment && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                  href={selectedRow.cancel_attachment}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    borderRadius: "6px",
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#ffebee" },
                  }}
                >
                  View Attachment
                </Button>
              )}
            </Box>
          )}

          {/* Two action cards */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              mt: 1,
            }}
          >
            {/* Re-order card */}
            <Box
              onClick={!reorderLoading ? handleChooseReorder : undefined}
              sx={{
                flex: 1,
                border: "2px solid #1565C0",
                borderRadius: "10px",
                p: 2.5,
                cursor: reorderLoading ? "not-allowed" : "pointer",
                opacity: reorderLoading ? 0.7 : 1,
                backgroundColor: "#f0f6ff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                transition: "all 0.2s",
                "&:hover": reorderLoading
                  ? {}
                  : {
                      backgroundColor: "#dbeafe",
                      boxShadow: "0 4px 16px rgba(21,101,192,0.15)",
                      transform: "translateY(-2px)",
                    },
              }}
            >
              {reorderLoading ? (
                <CircularProgress size={32} sx={{ color: "#1565C0" }} />
              ) : (
                <ReplayIcon sx={{ fontSize: 36, color: "#1565C0" }} />
              )}
              <Typography
                sx={{ fontWeight: 700, color: "#1565C0", fontSize: "1rem" }}
              >
                Re-order
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#475569",
                  textAlign: "center",
                  fontSize: "0.8rem",
                }}
              >
                Approve cancel &amp; create a new order with cancel reference
              </Typography>
            </Box>

            {/* Refund card */}
            <Box
              onClick={!reorderLoading ? handleChooseRefund : undefined}
              sx={{
                flex: 1,
                border: "2px solid #388e3c",
                borderRadius: "10px",
                p: 2.5,
                cursor: reorderLoading ? "not-allowed" : "pointer",
                opacity: reorderLoading ? 0.7 : 1,
                backgroundColor: "#f0fdf4",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                transition: "all 0.2s",
                "&:hover": reorderLoading
                  ? {}
                  : {
                      backgroundColor: "#dcfce7",
                      boxShadow: "0 4px 16px rgba(56,142,60,0.15)",
                      transform: "translateY(-2px)",
                    },
              }}
            >
              <CurrencyRupeeIcon sx={{ fontSize: 36, color: "#388e3c" }} />
              <Typography
                sx={{ fontWeight: 700, color: "#388e3c", fontSize: "1rem" }}
              >
                Refund
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#475569",
                  textAlign: "center",
                  fontSize: "0.8rem",
                }}
              >
                Approve cancel &amp; process a refund with custom percentage
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            mr: 2,
            mb: 2,
            mt: 1,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={() => setApproveModalOpen(false)}
            disabled={reorderLoading}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              px: 2.5,
              borderColor: "#e2e8f0",
              color: "#64748b",
              "&:hover": { borderColor: "#cbd5e1", backgroundColor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== STEP-2a: REFUND DIALOG ==================== */}
      <Dialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: isMobile ? "95vw" : "auto",
            m: isMobile ? 1 : 2,
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CurrencyRupeeIcon sx={{ color: "success.main" }} /> Process Refund
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ mb: 2, color: "text.secondary", fontSize: "0.9rem" }}>
            Specify the refund percentage for order{" "}
            <strong>{selectedRow?.order_number}</strong>:
          </Typography>

          {/* Warning / Notice about previous status */}
          {previousStatus && (
            <Alert
              severity={
                previousStatus === "order_placed" ? "info" : "warning"
              }
              sx={{ mb: 2, borderRadius: "8px" }}
            >
              Order status before cancel was:{" "}
              <strong>{formatStatus(previousStatus)}</strong>.
              {previousStatus === "order_placed"
                ? " Full refund (100%) is recommended."
                : " Production already started — 0% refund is recommended, but you can adjust below."}
            </Alert>
          )}

          {/* Financial summary card */}
          <Box
            sx={{
              backgroundColor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              p: 2,
              mb: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Total Order Amount:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ₹{totalAmount.toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Paid Amount (Base for refund):
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "success.main" }}
              >
                ₹{paidAmount.toFixed(2)}
              </Typography>
            </Box>

            {calculatedAmount !== null && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderTop: "1px dashed #cbd5e1",
                  pt: 0.75,
                  mt: 0.25,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#1e293b" }}
                >
                  Calculated Refund Amount:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    fontSize: "1rem",
                  }}
                >
                  ₹{calculatedAmount}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Quick-fill preset buttons */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block", mb: 0.75 }}
            >
              Quick Presets:
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {[0, 25, 50, 75, 100].map((pct) => (
                <Chip
                  key={pct}
                  label={`${pct}%`}
                  onClick={() => setPercentage(String(pct))}
                  variant={percentage === String(pct) ? "filled" : "outlined"}
                  color={percentage === String(pct) ? "primary" : "default"}
                  size="small"
                  sx={{
                    cursor: "pointer",
                    fontWeight: 600,
                    borderRadius: "6px",
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Percentage input */}
          <TextField
            autoFocus
            label="Refund Percentage (%)"
            type="number"
            fullWidth
            value={percentage}
            onChange={(e) => {
              const val = e.target.value;
              if (
                val === "" ||
                (parseFloat(val) >= 0 && parseFloat(val) <= 100)
              ) {
                setPercentage(val);
              }
            }}
            inputProps={{ min: 0, max: 100, step: "any" }}
            helperText={
              parsedPercentage > 100
                ? "Percentage cannot exceed 100%"
                : parsedPercentage < 0
                ? "Percentage cannot be negative"
                : calculatedAmount !== null
                ? `Customer will be refunded ₹${calculatedAmount} (${percentage}%)`
                : "Enter a percentage between 0 and 100"
            }
            error={parsedPercentage < 0 || parsedPercentage > 100}
            size="small"
          />
        </DialogContent>

        <DialogActions
          sx={{
            mr: 2,
            mb: 2,
            mt: 1,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {/* Back button to return to Step-1 */}
          <Button
            onClick={() => {
              setRefundDialogOpen(false);
              setApproveModalOpen(true);
            }}
            variant="text"
            sx={{ textTransform: "none", color: "#64748b" }}
          >
            ← Back
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              onClick={() => {
                setRefundDialogOpen(false);
                setDeleteId(null);
                setSelectedRow(null);
                setPercentage("");
              }}
              variant="outlined"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                borderColor: "#e2e8f0",
                color: "#64748b",
                "&:hover": {
                  borderColor: "#cbd5e1",
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveWithRefund}
              variant="contained"
              color="success"
              disabled={
                percentage === "" ||
                isNaN(parsedPercentage) ||
                parsedPercentage < 0 ||
                parsedPercentage > 100
              }
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
              }}
            >
              Confirm Refund
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ==================== REJECT DIALOG ==================== */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: isMobile ? "95vw" : "auto",
            m: isMobile ? 1 : 2,
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "error.main",
          }}
        >
          <ClearIcon /> Reject Cancel Request
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ mb: 2, color: "text.secondary" }}>
            Provide a reason for rejecting the cancel request for order{" "}
            <strong>{selectedRow?.order_number}</strong>:
          </Typography>

          <TextField
            autoFocus
            label="Rejection Reason"
            fullWidth
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="e.g. Order is already in delivery phase..."
            size="small"
          />
        </DialogContent>

        <DialogActions sx={{ mr: 2, mb: 2, mt: 1 }}>
          <Button
            onClick={() => {
              setRejectDialogOpen(false);
              setDeleteId(null);
              setSelectedRow(null);
              setRejectionReason("");
            }}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              borderColor: "#e2e8f0",
              color: "#64748b",
              "&:hover": {
                borderColor: "#cbd5e1",
                backgroundColor: "#f8fafc",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleRejectDelete(deleteId)}
            variant="contained"
            color="error"
            disabled={!rejectionReason.trim()}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
            }}
          >
            Reject Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== VIEW DETAILS DIALOG ==================== */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            width: isMobile ? "95vw" : "auto",
            m: isMobile ? 1 : 2,
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoIcon sx={{ color: "info.main" }} />
            <span>Cancel Details - #{selectedRow?.order_number}</span>
          </Box>
          {selectedRow?.status && (
            <Chip
              label={
                selectedRow.status === "cancelled"
                  ? "Approved & Cancelled"
                  : selectedRow.status === "refunded"
                  ? "Refunded"
                  : selectedRow.status === "partially_refunded"
                  ? "Partially Refunded"
                  : formatStatus(selectedRow.status)
              }
              size="small"
              sx={{
                fontWeight: 600,
                borderRadius: "8px",
                backgroundColor:
                  selectedRow.status === "cancelled"
                    ? "#dcfce7"
                    : selectedRow.status === "refunded" ||
                      selectedRow.status === "partially_refunded"
                    ? "#e0f2fe"
                    : "#f1f5f9",
                color:
                  selectedRow.status === "cancelled"
                    ? "#166534"
                    : selectedRow.status === "refunded" ||
                      selectedRow.status === "partially_refunded"
                    ? "#0369a1"
                    : "#475569",
              }}
            />
          )}
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2, pb: 2 }}>
          {selectedRow && (() => {
            const { paidBeforeRefund, refundAmt, refundPct } =
              getViewRefundDetails(selectedRow);

            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Section 1: Customer & Order Overview */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: "#475569",
                      mb: 1.5,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Order Information
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr"
                        : "repeat(2, 1fr)",
                      gap: 1.5,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Customer Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedRow.customerName || "-"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Branch
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedRow.branchName || "-"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Order Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedRow.order_date
                          ? (() => {
                              const cleanStr = String(selectedRow.order_date)
                                .split("T")[0]
                                .split(" ")[0];
                              const parts = cleanStr.split("-");
                              if (parts.length === 3) {
                                const [year, month, day] = parts;
                                return `${day}-${month}-${year}`;
                              }
                              return cleanStr;
                            })()
                          : "-"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Delivery Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedRow.delivery_date
                          ? (() => {
                              const cleanStr = String(selectedRow.delivery_date)
                                .split("T")[0]
                                .split(" ")[0];
                              const parts = cleanStr.split("-");
                              if (parts.length === 3) {
                                const [year, month, day] = parts;
                                return `${day}-${month}-${year}`;
                              }
                              return cleanStr;
                            })()
                          : "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider />

                {/* Section 2: Cancellation Reason & Attachments */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: "#475569",
                      mb: 1.5,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Cancellation Request Details
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
                    >
                      Cancel Reason / Remarks
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#334155",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedRow.cancel_remarks || "No remarks provided."}
                    </Typography>
                  </Box>
                  {selectedRow.cancel_attachment && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                      href={selectedRow.cancel_attachment}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        mt: 1,
                        textTransform: "none",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        borderRadius: "6px",
                      }}
                    >
                      View Cancellation Attachment
                    </Button>
                  )}
                </Box>

                <Divider />

                {/* Section 3: Financial Summary & Refund */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: "#475569",
                      mb: 1.5,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Financial Summary
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Total Order Amount
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ₹{(parseFloat(selectedRow.total_amount) || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Paid Amount
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ₹{paidBeforeRefund.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                        borderTop: "1px dashed #e2e8f0",
                        pt: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 500 }}
                      >
                        Refund Percentage
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "success.main" }}
                      >
                        {refundPct}%
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 500 }}
                      >
                        Refund Amount
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: "success.main",
                          fontSize: "1.05rem",
                        }}
                      >
                        ₹{refundAmt.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>

        <DialogActions sx={{ mr: 2, mb: 2, mt: 1 }}>
          <Button
            onClick={() => setViewDialogOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: Color.secondary.main,
              color: "#ffffff",
              "&:hover": { backgroundColor: Color.secondary.dark },
              textTransform: "none",
              px: 3,
              borderRadius: "8px",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ==========================================
// 2. PAYMENT APPROVAL SECTION
// ==========================================
const PaymentApprovalSection = ({ onCountChange }) => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Modals state
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await getAllBranches();
        if (res?.data?.data) {
          setBranches(res.data.data);
        }
      } catch (err) {
        console.error("Failed to load branches:", err);
      }
    };
    fetchBranches();
  }, []);

  const triggerSidebarRefresh = () => {
    window.dispatchEvent(new CustomEvent("sidebarRefresh"));
  };

  // Fetch pending payments
  const fetchPendingPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedBranch) params.branch_id = selectedBranch;
      if (searchTerm) params.search = searchTerm;

      const res = await getPendingPaymentApprovals(params);
      const data = res?.data?.data?.data || res?.data?.data || [];

      if (Array.isArray(data)) {
        const formatted = data.map((row) => ({
          ...row,
          orderNumber: row.order?.order_number || "N/A",
          orderId: row.order?.id,
          orderTotal: row.order?.total_amount || 0,
          customerName: row.order?.customer?.name || "N/A",
          customerPhone: row.order?.customer?.phone || "N/A",
          branchName: row.order?.branch?.name || "N/A",
          submittedBy: row.created_by_user?.name || row.cashier_name || "N/A",
        }));
        setRows(formatted);
        if (onCountChange) onCountChange(formatted.length);
      } else {
        setRows([]);
        if (onCountChange) onCountChange(0);
      }
    } catch (error) {
      console.error("Fetch pending payments error:", error);
      showSnackbar(
        error?.response?.data?.message ||
          "Failed to fetch pending payment approvals!",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, searchTerm, showSnackbar, onCountChange]);

  useEffect(() => {
    fetchPendingPayments();
  }, [fetchPendingPayments]);

  // Handle Approve Action
  const handleApproveConfirm = async () => {
    if (!selectedPayment) return;
    setActionLoading(true);
    try {
      const res = await approvePayment(selectedPayment.id);
      showSnackbar(
        res?.data?.message || "Payment approved successfully!",
        "success"
      );
      setApproveModalOpen(false);
      setSelectedPayment(null);
      triggerSidebarRefresh();
      fetchPendingPayments();
    } catch (error) {
      console.error("Approve payment error:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to approve payment!",
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Reject Action
  const handleRejectConfirm = async () => {
    if (!selectedPayment) return;
    setActionLoading(true);
    try {
      const res = await rejectPayment(selectedPayment.id, {
        rejection_reason: rejectionReason || "Payment rejected by admin.",
      });
      showSnackbar(
        res?.data?.message || "Payment rejected successfully!",
        "success"
      );
      setRejectModalOpen(false);
      setSelectedPayment(null);
      setRejectionReason("");
      triggerSidebarRefresh();
      fetchPendingPayments();
    } catch (error) {
      console.error("Reject payment error:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to reject payment!",
        "error"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      field: "orderNumber",
      headerName: "Order #",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: Color.primary.main,
              cursor: "pointer",
              "&:hover": { textDecoration: "none" },
            }}
            onClick={() => navigate(`/view-order/${params.row.orderId}`)}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "customerName",
      headerName: "Customer",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            py: 0.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#1e293b", lineHeight: 1.3 }}
          >
            {params.row.customerName || "-"}
          </Typography>
          {params.row.customerPhone && (
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontSize: "0.75rem", lineHeight: 1.2 }}
            >
              {params.row.customerPhone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "branchName",
      headerName: "Branch",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ color: "#334155" }}>
            {params.value || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "amount",
      headerName: "Amount (₹)",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: "#166534" }}
          >
            ₹{Number(params.value).toFixed(2)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "payment_method",
      headerName: "Payment Method",
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip
            label={(params.value || "cash").toUpperCase()}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.72rem",
              backgroundColor: "#e0f2f1",
              color: "#00695c",
              borderRadius: "4px",
              height: 22,
            }}
          />
        </Box>
      ),
    },
    {
      field: "submittedBy",
      headerName: "Submitted By",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ color: "#475569" }}>
            {params.value || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "payment_date",
      headerName: "Date",
      flex: 1.1,
      minWidth: 160,
      renderCell: (params) => {
        const val = params.value;
        if (!val) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>-</Typography>
            </Box>
          );
        }
        try {
          const d = new Date(val);
          if (isNaN(d.getTime())) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                <Typography variant="caption">{String(val)}</Typography>
              </Box>
            );
          }
          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = d.getFullYear();
          let hours = d.getHours();
          const minutes = String(d.getMinutes()).padStart(2, "0");
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12 || 12;
          const formatted = `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Typography
                variant="caption"
                sx={{ fontSize: "0.78rem", color: "#475569" }}
              >
                {formatted}
              </Typography>
            </Box>
          );
        } catch (e) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Typography variant="caption">{String(val)}</Typography>
            </Box>
          );
        }
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.1,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: "100%",
          }}
        >
          <Tooltip title="View Order">
            <IconButton
              size="small"
              color="info"
              sx={{
                backgroundColor: "rgba(2, 136, 209, 0.08)",
                "&:hover": { backgroundColor: "rgba(2, 136, 209, 0.2)" },
                borderRadius: "6px",
                p: 0.6,
              }}
              onClick={() => navigate(`/view-order/${params.row.orderId}`)}
            >
              <VisibilityIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Approve Payment">
            <IconButton
              size="small"
              sx={{
                color: "#166534",
                backgroundColor: "#e8f5e9",
                "&:hover": { backgroundColor: "#c8e6c9" },
                borderRadius: "6px",
                p: 0.6,
              }}
              onClick={() => {
                setSelectedPayment(params.row);
                setApproveModalOpen(true);
              }}
            >
              <CheckIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reject Payment">
            <IconButton
              size="small"
              sx={{
                color: "#d32f2f",
                backgroundColor: "#ffebee",
                "&:hover": { backgroundColor: "#ffcdd2" },
                borderRadius: "6px",
                p: 0.6,
              }}
              onClick={() => {
                setSelectedPayment(params.row);
                setRejectionReason("");
                setRejectModalOpen(true);
              }}
            >
              <ClearIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 1 }}>
      {/* Top Action Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: "#334155" }}
            >
              Manager Payment Approvals
            </Typography>
            {rows.length > 0 && (
              <Chip
                label={`${rows.length} Pending`}
                size="small"
                sx={{
                  bgcolor: "#fff7ed",
                  color: "#c2410c",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  height: 22,
                  borderRadius: "4px",
                }}
              />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Review and approve manager submitted advance and full payments
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchPendingPayments}
          disabled={loading}
          size="small"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#cbd5e1",
            color: "#475569",
            "&:hover": {
              borderColor: Color.secondary.main,
              color: Color.secondary.main,
              backgroundColor: "rgba(124, 11, 204, 0.04)",
            },
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Filter Card */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Order #, Customer, Ref..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: "action.active", mr: 1 }} />
                ),
              }}
            />
          </Grid>
          {branches.length > 0 && (
            <Grid item xs={12} sm={4} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                  value={selectedBranch}
                  label="Branch"
                  onChange={(e) => setSelectedBranch(e.target.value)}
                >
                  <MenuItem value="">All Branches</MenuItem>
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Table */}
      <Paper
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          rowHeight={58}
          getRowId={(row) => row.id}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableSelectionOnClick
          disableColumnMenu
          sx={{
            fontFamily: '"Poppins", sans-serif',
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f8fafc",
              fontWeight: 700,
              fontSize: isMobile ? "13px" : "15px",
              borderBottom: "1px solid #e2e8f0",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              fontSize: isMobile ? "12px" : "14px",
              borderBottom: "1px solid #f1f5f9",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fafafa",
            },
            border: "none",
          }}
        />
      </Paper>

      {/* Approve Modal */}
      <Dialog
        open={approveModalOpen}
        onClose={() => !actionLoading && setApproveModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Approve Payment</DialogTitle>
        <DialogContent dividers>
          {selectedPayment && (
            <Box sx={{ py: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Are you sure you want to approve this payment?
              </Typography>
              <Card
                variant="outlined"
                sx={{ bgcolor: "#f9fbe7", p: 1.5, borderRadius: 2, mb: 1 }}
              >
                <Typography variant="body2">
                  <strong>Order Number:</strong> {selectedPayment.orderNumber}
                </Typography>
                <Typography variant="body2">
                  <strong>Amount:</strong> ₹
                  {Number(selectedPayment.amount).toFixed(2)}
                </Typography>
                <Typography variant="body2">
                  <strong>Method:</strong>{" "}
                  {(selectedPayment.payment_method || "cash").toUpperCase()}
                </Typography>
                <Typography variant="body2">
                  <strong>Submitted By:</strong> {selectedPayment.submittedBy}
                </Typography>
              </Card>
              <Typography variant="caption" color="text.secondary">
                Upon approval, this payment will be recorded in revenue counts
                and a WhatsApp receipt notification will be sent to the
                customer.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveModalOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveConfirm}
            disabled={actionLoading}
            startIcon={
              actionLoading ? <CircularProgress size={18} /> : <CheckIcon />
            }
          >
            Approve Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => !actionLoading && setRejectModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#d32f2f" }}>
          Reject Payment
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayment && (
            <Box sx={{ py: 1 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Provide a reason for rejecting payment of{" "}
                <strong>
                  ₹{Number(selectedPayment.amount).toFixed(2)}
                </strong>{" "}
                for Order <strong>#{selectedPayment.orderNumber}</strong>:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Rejection Reason"
                placeholder="Enter reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectModalOpen(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={actionLoading}
            startIcon={
              actionLoading ? <CircularProgress size={18} /> : <ClearIcon />
            }
          >
            Reject Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ==========================================
// 3. MAIN APPROVAL PAGE (WITH ORDER & PAYMENT TABS)
// ==========================================
const Approval = ({ defaultTab }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromQuery = searchParams.get("tab");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeTab, setActiveTab] = useState(() => {
    if (defaultTab === "payment" || tabFromQuery === "payment") return "payment";
    return "order";
  });

  const [orderCount, setOrderCount] = useState(0);
  const [paymentCount, setPaymentCount] = useState(0);

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    } else if (tabFromQuery === "payment" || tabFromQuery === "order") {
      setActiveTab(tabFromQuery);
    }
  }, [defaultTab, tabFromQuery]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", newValue);
      return next;
    });
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#1e293b",
              fontFamily: '"Poppins", sans-serif',
            }}
          >
            Approvals
          </Typography>
          {orderCount + paymentCount > 0 && (
            <Chip
              label={`${orderCount + paymentCount} Total Pending`}
              size="small"
              sx={{
                bgcolor: "#fee2e2",
                color: "#991b1b",
                fontWeight: 700,
                fontSize: "0.75rem",
                borderRadius: "6px",
                height: 24,
              }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            mt: 0.5,
            fontFamily: '"Poppins", sans-serif',
          }}
        >
          Review and approve order cancellations and manager payment submissions
        </Typography>
      </Box>

      {/* Main Tabs (Order / Payment) */}
      <Box
        sx={{
          mb: 2.5,
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          borderRadius: "10px",
          p: 0.5,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            minHeight: "44px",
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              backgroundColor: Color.secondary.main,
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? "0.85rem" : "0.95rem",
              minHeight: "44px",
              py: 1,
              px: { xs: 2, sm: 3 },
              color: "#64748b",
              fontFamily: '"Poppins", sans-serif',
              "&.Mui-selected": {
                color: Color.secondary.main,
              },
            },
          }}
        >
          <Tab
            value="order"
            icon={<ShoppingCartIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>Order</span>
                {orderCount > 0 && (
                  <Chip
                    label={orderCount}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      backgroundColor:
                        activeTab === "order" ? Color.secondary.main : "#f1f5f9",
                      color: activeTab === "order" ? "#ffffff" : "#475569",
                      transition: "all 0.2s",
                    }}
                  />
                )}
              </Box>
            }
          />
          <Tab
            value="payment"
            icon={<CurrencyRupeeIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>Payment</span>
                {paymentCount > 0 && (
                  <Chip
                    label={paymentCount}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      backgroundColor:
                        activeTab === "payment" ? Color.secondary.main : "#f1f5f9",
                      color: activeTab === "payment" ? "#ffffff" : "#475569",
                      transition: "all 0.2s",
                    }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Contents */}
      <Box sx={{ display: activeTab === "order" ? "block" : "none" }}>
        <OrderApprovalSection onCountChange={setOrderCount} />
      </Box>
      <Box sx={{ display: activeTab === "payment" ? "block" : "none" }}>
        <PaymentApprovalSection onCountChange={setPaymentCount} />
      </Box>
    </Box>
  );
};

export { OrderApprovalSection, PaymentApprovalSection };
export default Approval;